import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { HelmetProvider } from 'react-helmet-async';
import { ScrollToTop } from "@/components/ScrollToTop";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { OnboardingTourProvider } from "@/contexts/OnboardingTourContext";
import { WelcomeMessagesProvider } from "@/contexts/WelcomeMessagesContext";
import { VendorNavCountsProvider } from "@/contexts/VendorNavCountsContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Layout } from "@/components/Layout";
import { WaitlistGate } from "@/components/WaitlistGate";
import LoginPage from "./app/auth/login/page";
import ForgotPasswordPage from "./app/auth/forgot-password/page";
import ResetPasswordPage from "./app/auth/reset-password/page";
import VerifyEmailPage from "./app/auth/verify-email/page";
import ConfirmEmailPage from "./app/auth/confirm/page";
import DashboardPage from "./app/dashboard/page";
import JobsPage from "./app/jobs/page";
import NewJobPage from "./app/jobs/new/page";
import JobDetailPage from "./app/jobs/[jobId]/page";
import EditJobPage from "./app/jobs/[jobId]/edit/page";
import JobExecutionPage from "./app/jobs/[jobId]/execution/page";
import JobReviewPage from "./app/jobs/[jobId]/review/page";
import JobMilestonesPage from "./app/jobs/[jobId]/milestones/page";
import PaymentSetupPage from "./app/jobs/[jobId]/payment-setup/page";
import RFQsPage from "./app/rfqs/page";
import RFQDetailPage from "./app/rfqs/[rfqId]/page";
import VendorBidsPage from "./app/bids/page";
import BidHistoryPage from "./app/bid-history/page";
import AdminDashboardPage from "./app/admin/dashboard/page";
import AdminOrganizationsPage from "./app/admin/organizations/page";
import AdminUsersPage from "./app/admin/users/page";
import VendorManagementPage from "./app/admin/vendors/page";
import VerificationPage from "./app/admin/verification/page";
import DisputeManagementPage from "./app/admin/disputes/page";
import SystemManagementPage from "./app/admin/system/page";
import OrganizationSettingsPage from "./app/settings/organization/page";
import AccountSettingsPage from "./app/settings/account/page";
import VendorProfilePage from "./app/profile/vendor/page";
import ClientProfilePage from "./app/profile/client/page";
import AdminProfilePage from "./app/profile/admin/page";
import OrganizationProfilePage from "./app/profile/organization/page";
import VendorSearchPage from "./app/vendors/search/page";
import VendorDetailPage from "./app/vendors/[vendorId]/page";
import VendorInvitePage from "./app/vendors/invite/page";
import AdminVendorInvitationsPage from "./app/admin/vendor-invitations/page";
import AdminSecurityPage from "./app/admin/security/page";
import SecurityMonitoringPage from "./app/admin/security-monitoring/page";
import AdminErrorsPage from "./app/admin/errors/page";
import AdminSpecificationsPage from "./app/admin/specifications/page";
import AdminMilestoneSetsPage from "./app/admin/milestones/page";
import AdminProductCategoriesPage from "./app/admin/product-categories/page";
import AdminSpecificationCategoriesPage from "./app/admin/specification-categories/page";
import AdminSpecificationFieldsPage from "./app/admin/specification-fields/page";
import AdminAIPromptsPage from "./app/admin/ai-prompts/page";
import AdminAIUsagePage from "./app/admin/ai-usage/page";
import AdminCapabilitiesPage from "./app/admin/capabilities/page";
import AdminCertificationsPage from "./app/admin/certifications/page";
import FeedbackPage from "./app/feedback/page";
import AdminFeedbackPage from "./app/admin/feedback/page";
import AdminPaymentsPage from "./app/admin/payments/page";
import AdminPaymentApprovalsPage from "./app/admin/payment-approvals/page";
import TraditionalAchApprovalsPage from "./app/admin/traditional-ach/page";
import FixPaymentPage from "./app/admin/fix-payment/page";
import AdjustmentLogsPage from "./app/admin/adjustment-logs/page";
import AdminMassCommunicationsPage from "./app/admin/mass-communications/page";
import AdminWelcomeMessagesPage from "./app/admin/welcome-messages/page";
import AdminAnalyticsPage from "./app/admin/analytics/page";
import AdminWaitlistPage from "./app/admin/waitlist/page";
import AdminJobsPage from "./app/admin/jobs/page";
import AdminLegalDocumentsPage from "./app/admin/legal-documents/page";
import SpecificationsPage from "./app/specifications/page";
import MilestoneSetsPage from "./app/milestones/page";
import AddressesPage from "./app/addresses/page";
import CalendarPage from "./app/calendar/page";
import ReportsPage from "./app/reports/index";
import SpendReportPage from "./app/reports/spend/page";
import VendorsReportPage from "./app/reports/vendors/page";
import CycleTimesReportPage from "./app/reports/cycle-times/page";
import DiversityReportPage from "./app/reports/diversity/page";
import ProjectsPage from "./app/projects/page";
import PurchaseOrdersPage from "./app/purchase-orders/page";
import PurchaseOrderDetailPage from "./app/purchase-orders/[poId]/page";
import NotFound from "./pages/NotFound";
import Unauthorized from "./pages/Unauthorized";
import DesktopRequired from "./pages/DesktopRequired";
import Landing from "./pages/Landing";
import ForClients from "./pages/ForClients";
import ForAgencies from "./pages/ForAgencies";
import ForProcurement from "./pages/ForProcurement";
import ForVendors from "./pages/ForVendors";
import Features from "./pages/Features";
import HowItWorksClients from "./pages/HowItWorksClients";
import HowItWorksVendors from "./pages/HowItWorksVendors";
import TestSentry from "./pages/TestSentry";
import Bootstrap from "./pages/Bootstrap";
import Contact from "./pages/Contact";
import Insights from "./pages/Insights";
import InsightPost from "./pages/InsightPost";
import Pricing from "./pages/Pricing";
import Security from "./pages/Security";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import POTerms from "./pages/POTerms";
import Verification from "./pages/Verification";
import SupplierDiversity from "./pages/SupplierDiversity";
import { errorTracking, isPostgrestError, extractTableFromQueryKey } from "@/lib/errorTracking";
import { QueryCache, MutationCache } from "@tanstack/react-query";

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error: any, query) => {
      const isEdgeFunctionError = error?.name === 'EdgeFunctionError';
      const isSupabaseError = isPostgrestError(error);
      
      console.error('[React Query] Query error:', {
        queryKey: query.queryKey,
        error: error?.message || String(error),
        isEdgeFunctionError,
        isSupabaseError,
        functionName: isEdgeFunctionError ? error.functionName : undefined,
        httpStatus: isEdgeFunctionError ? error.httpStatus : undefined,
        postgresCode: isSupabaseError ? error.code : undefined,
      });
      
      // Route to specialized handlers based on error type
      if (isEdgeFunctionError) {
        errorTracking.captureEdgeFunctionError(error, {
          queryKey: query.queryKey,
        });
      } else if (isSupabaseError) {
        // Handle Supabase PostgrestError with rich context
        const tableName = extractTableFromQueryKey(query.queryKey);
        errorTracking.captureSupabaseError(error, {
          table: tableName,
          operation: 'select',
          queryKey: query.queryKey,
        });
      } else {
        // Standard Error or unknown error type
        errorTracking.captureQueryError(
          error instanceof Error ? error : new Error(String(error)),
          'query',
          query.queryKey,
          undefined
        );
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error: any, variables, context, mutation) => {
      const isEdgeFunctionError = error?.name === 'EdgeFunctionError';
      const isSupabaseError = isPostgrestError(error);
      
      console.error('[React Query] Mutation error:', {
        mutationKey: mutation.options.mutationKey,
        error: error?.message || String(error),
        isEdgeFunctionError,
        isSupabaseError,
        functionName: isEdgeFunctionError ? error.functionName : undefined,
        httpStatus: isEdgeFunctionError ? error.httpStatus : undefined,
        postgresCode: isSupabaseError ? error.code : undefined,
      });
      
      // Route to specialized handlers based on error type
      if (isEdgeFunctionError) {
        errorTracking.captureEdgeFunctionError(error, {
          mutationKey: mutation.options.mutationKey,
          variables: variables,
        });
      } else if (isSupabaseError) {
        // Handle Supabase PostgrestError with rich context
        const tableName = mutation.options.mutationKey 
          ? extractTableFromQueryKey(mutation.options.mutationKey as unknown[])
          : undefined;
        errorTracking.captureSupabaseError(error, {
          table: tableName,
          operation: 'insert', // Mutations are typically writes
          mutationKey: mutation.options.mutationKey,
          variables: variables,
        });
      } else {
        // Standard Error or unknown error type
        errorTracking.captureQueryError(
          error instanceof Error ? error : new Error(String(error)),
          'mutation',
          mutation.options.mutationKey,
          variables
        );
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

const AppRoutes = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public marketing routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/features" element={<Features />} />
      <Route path="/for-clients" element={<ForClients />} />
      <Route path="/for-agencies" element={<ForAgencies />} />
      <Route path="/for-procurement" element={<ForProcurement />} />
      <Route path="/for-vendors" element={<ForVendors />} />
      <Route path="/how-it-works/clients" element={<HowItWorksClients />} />
      <Route path="/how-it-works/vendors" element={<HowItWorksVendors />} />
      <Route path="/test-sentry" element={<TestSentry />} />
      <Route path="/bootstrap" element={<Bootstrap />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/security" element={<Security />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/po-terms" element={<POTerms />} />
      <Route path="/verification" element={<Verification />} />
      <Route path="/supplier-diversity" element={<SupplierDiversity />} />
      <Route path="/insights" element={<Insights />} />
      <Route path="/insights/:slug" element={<InsightPost />} />
      
      {/* Public auth routes - allow access if not authenticated OR if MFA pending */}
      <Route path="/auth/signup" element={!isAuthenticated ? <WaitlistGate /> : <Navigate to="/dashboard" />} />
      <Route path="/auth/login" element={<LoginPage />} />
      <Route path="/auth/forgot-password" element={!isAuthenticated ? <ForgotPasswordPage /> : <Navigate to="/dashboard" />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
      <Route path="/auth/confirm" element={<ConfirmEmailPage />} />
      <Route path="/desktop-required" element={<DesktopRequired />} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout>
            <DashboardPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/jobs" element={
        <ProtectedRoute>
          <Layout>
            <JobsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/jobs/new" element={
        <ProtectedRoute>
          <Layout>
            <NewJobPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/jobs/:jobId" element={
        <ProtectedRoute>
          <Layout>
            <JobDetailPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/jobs/:jobId/edit" element={
        <ProtectedRoute>
          <Layout>
            <EditJobPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/jobs/:jobId/execution" element={
        <ProtectedRoute>
          <Layout>
            <JobExecutionPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/jobs/:jobId/review" element={
        <ProtectedRoute>
          <Layout>
            <JobReviewPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/jobs/:jobId/milestones" element={
        <ProtectedRoute>
          <Layout>
            <JobMilestonesPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/jobs/:jobId/payment-setup" element={
        <ProtectedRoute>
          <Layout>
            <PaymentSetupPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/rfqs" element={
        <ProtectedRoute>
          <Layout>
            <RFQsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/rfqs/:rfqId" element={
        <ProtectedRoute>
          <Layout>
            <RFQDetailPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/bids" element={
        <ProtectedRoute>
          <Layout>
            <VendorBidsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/bid-history" element={
        <ProtectedRoute allowedRoles={['vendor']}>
          <Layout>
            <BidHistoryPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/projects" element={
        <ProtectedRoute allowedRoles={['client']}>
          <Layout>
            <ProjectsPage />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/specifications" element={
        <ProtectedRoute allowedRoles={['client']}>
          <Layout>
            <SpecificationsPage />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/milestones" element={
        <ProtectedRoute allowedRoles={['client']}>
          <Layout>
            <MilestoneSetsPage />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/addresses" element={
        <ProtectedRoute allowedRoles={['client']}>
          <Layout>
            <AddressesPage />
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/calendar" element={
        <ProtectedRoute>
          <Layout>
            <CalendarPage />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Reports routes */}
      <Route path="/reports" element={
        <ProtectedRoute allowedRoles={['client']}>
          <Layout>
            <ReportsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/reports/spend" element={
        <ProtectedRoute allowedRoles={['client']}>
          <Layout>
            <SpendReportPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/reports/vendors" element={
        <ProtectedRoute allowedRoles={['client']}>
          <Layout>
            <VendorsReportPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/reports/cycle-times" element={
        <ProtectedRoute allowedRoles={['client']}>
          <Layout>
            <CycleTimesReportPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/reports/diversity" element={
        <ProtectedRoute allowedRoles={['client']}>
          <Layout>
            <DiversityReportPage />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Purchase Order routes */}
      <Route path="/purchase-orders" element={
        <ProtectedRoute>
          <Layout>
            <PurchaseOrdersPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/purchase-orders/:poId" element={
        <ProtectedRoute>
          <Layout>
            <PurchaseOrderDetailPage />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Admin routes */}
      <Route path="/admin/dashboard" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminDashboardPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/jobs" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminJobsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/organizations" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminOrganizationsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminUsersPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/vendors" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <VendorManagementPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/verification" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <VerificationPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/disputes" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <DisputeManagementPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/security" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminSecurityPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/security-monitoring" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <SecurityMonitoringPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/errors" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminErrorsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/specifications" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminSpecificationsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/milestones" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminMilestoneSetsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/product-categories" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminProductCategoriesPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/specification-categories" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminSpecificationCategoriesPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/specification-fields" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminSpecificationFieldsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/ai-prompts" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminAIPromptsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/ai-usage" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminAIUsagePage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/capabilities" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminCapabilitiesPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/certifications" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminCertificationsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/legal-documents" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminLegalDocumentsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/payments" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminPaymentsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/payment-approvals" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminPaymentApprovalsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/fix-payment" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <FixPaymentPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/traditional-ach" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <TraditionalAchApprovalsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/adjustment-logs" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdjustmentLogsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/system" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <SystemManagementPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/mass-communications" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminMassCommunicationsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/feedback" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminFeedbackPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/welcome-messages" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminWelcomeMessagesPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/feedback" element={
        <ProtectedRoute>
          <FeedbackPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/vendor-invitations" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminVendorInvitationsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/waitlist" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminWaitlistPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminAnalyticsPage />
        </ProtectedRoute>
      } />
      
      {/* Settings routes */}
      <Route path="/settings/organization" element={
        <ProtectedRoute>
          <Layout>
            <OrganizationSettingsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings/account" element={
        <ProtectedRoute>
          <Layout>
            <AccountSettingsPage />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Profile routes */}
      <Route path="/profile/vendor" element={
        <ProtectedRoute>
          <Layout>
            <VendorProfilePage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/profile/client" element={
        <ProtectedRoute>
          <Layout>
            <ClientProfilePage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/profile/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout>
            <AdminProfilePage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/profile/organization" element={
        <ProtectedRoute>
          <Layout>
            <OrganizationProfilePage />
          </Layout>
        </ProtectedRoute>
      } />
      
      {/* Vendor discovery routes */}
      <Route path="/vendors/search" element={
        <ProtectedRoute>
          <Layout>
            <VendorSearchPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/vendors/:vendorId" element={
        <ProtectedRoute>
          <Layout>
            <VendorDetailPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/vendors/invite" element={
        <ProtectedRoute allowedRoles={['client', 'admin']}>
          <Layout>
            <VendorInvitePage />
          </Layout>
        </ProtectedRoute>
      } />
      
      
      {/* Unauthorized access */}
      <Route path="/unauthorized" element={<Unauthorized />} />
      
      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <VendorNavCountsProvider>
              <OnboardingTourProvider>
                <WelcomeMessagesProvider>
                  <ScrollToTop />
                  <Toaster />
                  <Sonner />
                  <AppRoutes />
                </WelcomeMessagesProvider>
              </OnboardingTourProvider>
            </VendorNavCountsProvider>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
